/**
 * Map zoom level constants for consistent zoom levels across the application.
 * 
 * Google Maps zoom levels:
 * - 1-5: World/continent level
 * - 6-9: Country/regional level
 * - 10-12: City level
 * - 13-15: Street level
 * - 16-21: Building level
 */

export const MAP_ZOOM = {
    // World view (e.g. /results keyword search); 3 avoids grey area at top of tall map containers
    WORLD: 3,

    // Country search on Results (all tabs)
    COUNTRY_SEARCH: 6,

    // City search on Results (all tabs)
    CITY_SEARCH: 10,

    // Regional/Country level
    REGIONAL: 9,

    // City level (appropriate for city overview)
    CITY: 11,
    
    // Street level (for detailed location views)
    STREET: 13,
    
    // Focused location (for individual location detail)
    FOCUSED: 15,
} as const;

/**
 * Default zoom levels for different map contexts
 */
export const DEFAULT_ZOOM = {
    // Default when no specific location is selected
    NO_LOCATION: MAP_ZOOM.REGIONAL,
    
    // Default for city-level views
    CITY: MAP_ZOOM.STREET, // 13 - street level for city views
    
    // Default for user location (more detailed)
    USER_LOCATION: MAP_ZOOM.STREET,
    
    // Default for map component when showing markers
    MAP_COMPONENT: MAP_ZOOM.STREET, // 13 - street level for city views
} as const;

/**
 * Map coordinate adjustments
 * 
 * These offsets compensate for visual centering issues when maps are displayed
 * in tall containers (e.g., 250vh height) where the top portion is above the viewport.
 * 
 * When a map is taller than the viewport, the visual center (what users see) appears
 * to be north of the actual coordinate center because the top portion is off-screen.
 * 
 * This offset shifts coordinates south so that the visual center aligns with the
 * intended location (e.g., downtown/tourist area for cities).
 * 
 * @example
 * // If map center should be at lat: 36.1716, but map is tall and top is above viewport,
 * // applying -0.12 offset makes the visual center appear at the correct location
 * const adjustedLat = originalLat + MAP_COORDINATE_OFFSET.LATITUDE_SOUTH;
 */
export const MAP_COORDINATE_OFFSET = {
    /**
     * Latitude offset to shift map center south (in degrees)
     * 
     * -0.12 degrees ≈ -13.2 km (approximately 8.2 miles)
     * 
     * This compensates for tall maps (250vh) where the top is above the viewport,
     * causing the visual center to appear north of the actual coordinate center.
     * 
     * Adjust this value if:
     * - Map appears too far north: decrease (more negative, e.g., -0.15)
     * - Map appears too far south: increase (less negative, e.g., -0.10)
     * 
     * To calculate: 1 degree of latitude ≈ 111 km
     */
    LATITUDE_SOUTH: -0.12,
    
    /**
     * Longitude offset (currently unused, but available for future adjustments)
     * 
     * Set to 0 as no east/west adjustment is typically needed.
     */
    LONGITUDE: 0,
} as const;

/**
 * Default map center coordinates
 * Used as fallback when no location is available
 */
export const DEFAULT_MAP_CENTER = {
    // New York City coordinates (default fallback)
    NYC: { lat: 40.7128, lng: -74.006 },
    
    // San Francisco coordinates (alternative fallback)
    SF: { lat: 37.7749, lng: -122.4194 },
} as const;

/**
 * Default map location behavior
 * 
 * Controls what the map shows when no city is selected:
 * - "current" - Shows user's current location (uses geolocation API)
 * - "NYC" - Shows New York City as default
 * - "SF" - Shows San Francisco as default
 * - Or any other city name from DEFAULT_MAP_CENTER
 * 
 * Set to "NYC" to default to New York City instead of user location.
 */
export const DEFAULT_MAP_LOCATION: 'current' | keyof typeof DEFAULT_MAP_CENTER = 'NYC';

/**
 * Default location search radius in miles
 * Used for all location searches across the application
 * 
 * Backend API requirements (GET /api/v2/locations):
 * - radius: number (miles), default 10, must be > 0 and ≤ 500
 * - No conversion needed - backend expects miles directly
 */
export const DEFAULT_LOCATION_SEARCH_RADIUS = 50; // miles

/**
 * Default location search limit
 * Maximum number of locations to return from the API
 * 
 * Backend API requirements (GET /api/v2/locations):
 * - limit: integer, allowed range 1-100
 * - Backend default is 100 if omitted
 */
export const DEFAULT_LOCATION_SEARCH_LIMIT = 100;

/**
 * Clamps panning to the real world so dragging can't scroll past the map's edge into the grey/
 * tiled-repeat area that appears below MAP_ZOOM.WORLD's zoom level (±85° matches Web Mercator's
 * max latitude before projection distortion, the same bound Google Maps itself uses internally).
 * Pass to a `<Map restriction={...}>` (@vis.gl/react-google-maps) alongside minZoom={MAP_ZOOM.WORLD}.
 */
export const WORLD_BOUNDS_RESTRICTION = {
    latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
    strictBounds: true,
} as const;
